import { z } from "zod";

import { EnumValues, OrgRole } from "./enums.js";
import { isOrgRoleSuperset } from "./orgRole.js";
import { getNormalEnum } from "./pseudoEnums.js";
import {
  PartialUserInteractions,
  UserInteraction,
} from "./userInteractionTypes.js";

export const OnboardingTypeLiteral = z.enum(["TRIAL", "CANDIDATE", "FRIEND"]);

export type OnboardingType = z.infer<typeof OnboardingTypeLiteral>;
export const OnboardingType = getNormalEnum(OnboardingTypeLiteral);

export const LANGUAGE_SKIPPED = "SKIPPED";

export type LanguageSkipped = typeof LANGUAGE_SKIPPED;

export const LanguagePreferenceLiteral = z.enum([
  "SQL",
  "SQL_AND_PYTHON",
  "NO_CODE",
]);

export const LanguagePreferenceSource = {
  ONBOARDING: "ONBOARDING",
} as const;
export type LanguagePreferenceSource = EnumValues<
  typeof LanguagePreferenceSource
>;

export const LanguagePreference = getNormalEnum(LanguagePreferenceLiteral);

export type LanguagePreference = z.infer<typeof LanguagePreferenceLiteral>;

export const UserLanguagePreference = {
  ...LanguagePreference,
  SKIPPED: LANGUAGE_SKIPPED,
} as const;

export type UserLanguagePreference = EnumValues<typeof UserLanguagePreference>;

export const UseCaseLiteral = z.enum([
  "EDA",
  "INTERACTIVE_APPS",
  "SELF_SERVE",
  "EMBEDDED_APPS",
  "OTHER",
  // older values (pre 2025 questionnaire changes)
  "BI",
  "INTERNAL_TOOLS",
  "ML",
  "DATA_PIPELINING",
]);

export const UseCase = getNormalEnum(UseCaseLiteral);
export type UseCase = z.infer<typeof UseCaseLiteral>;

export const CollaborationIntentLiteral = z.enum(["JUST_ME", "WITH_A_TEAM"]);

export type CollaborationIntent = z.infer<typeof CollaborationIntentLiteral>;
export const CollaborationIntent = getNormalEnum(CollaborationIntentLiteral);

export const WorkspaceIntentLiteral = z.enum(["JOB", "SCHOOL", "PERSONAL"]);

export const CompanySizeLiteral = z.enum([
  "ONE_TO_TEN",
  "ELEVEN_TO_FIFTY",
  "FIFTY_ONE_TO_TWO_HUNDRED_AND_FIFTY",
  "TWO_HUNDRED_AND_FIFTY_ONE_TO_ONE_THOUSAND",
  "ONE_THOUSAND_PLUS",
]);
export type CompanySizeLiteral = z.infer<typeof CompanySizeLiteral>;

export const WorkspaceIntent = getNormalEnum(WorkspaceIntentLiteral);
export type WorkspaceIntent = z.infer<typeof WorkspaceIntentLiteral>;

export const PersonaTypeOfWorkLiteral = z.enum([
  "DATA_SCIENCE",
  "BUSINESS_INTELLIGENCE",
  "ENGINEERING",
  "PRODUCT_MANAGEMENT",
  "INFORMATION_TECHNOLOGY",
  "OPERATIONS",
  "SALES",
  "MARKETING",
  "FINANCE",
  "CUSTOMER_SERVICE_OR_SUPPORT",
  "OTHER",
]);
export type PersonaTypeOfWork = z.infer<typeof PersonaTypeOfWorkLiteral>;

export const PersonaRoleLiteral = z.enum([
  "NON_MANAGER",
  "MANAGER",
  "DIRECTOR",
  "VP_LEVEL",
  "C_LEVEL",
  "CONSULTANT_OR_CONTRACTOR",
]);
export type PersonaRole = z.infer<typeof PersonaRoleLiteral>;

export const UserLanguagePreferencesRawLiteral = z.enum([
  "PYTHON",
  "SQL",
  "NO_CODE",
]);
export type UserLanguagePreferencesRaw = z.infer<
  typeof UserLanguagePreferencesRawLiteral
>;

export const Persona = z.object({
  // Pre 2025 onboarding questionnaires
  useCases: z.array(UseCaseLiteral).optional(),
  useCaseOtherValues: z.array(z.string()).optional(),

  // Onboarding questionnaire revamp 2025
  typeOfWork: PersonaTypeOfWorkLiteral.optional(),
  role: PersonaRoleLiteral.optional(),
  // Pre and post 2025 onboarding questions
  language: LanguagePreferenceLiteral.or(z.null()).or(
    z.literal(LANGUAGE_SKIPPED),
  ),
  rawLanguages: z.array(UserLanguagePreferencesRawLiteral).optional(),
});
export type Persona = z.infer<typeof Persona>;

export const PersonaDefault = {
  language: null,
  rawLanguages: [],
};

const NEW_CREATOR_ONBOARDING_CUTOFF = new Date("2025-11-01T00:00:00Z");

interface UserFields {
  createdDate: Date;
  orgRole: OrgRole;
  persona: Persona | null;
  interactions: PartialUserInteractions | null;
}
export const showCreatorOnboarding = ({
  createdDate,
  interactions,
  orgRole,
}: UserFields): boolean => {
  const hasSeenNewCreatorOnboarding = interactions
    ? interactions[UserInteraction.CREATOR_ONBOARDING_COMPLETED]
    : false;
  return (
    createdDate > NEW_CREATOR_ONBOARDING_CUTOFF &&
    isOrgRoleSuperset(orgRole, OrgRole.EXPLORER) &&
    !hasSeenNewCreatorOnboarding
  );
};

export const convertRawLanguageOptionsToUserLanguagePreference = (
  rawLanguages: Set<UserLanguagePreferencesRaw>,
): Exclude<UserLanguagePreference, "SKIPPED"> => {
  let language: UserLanguagePreference = UserLanguagePreference.NO_CODE;
  if (rawLanguages.has("PYTHON")) {
    language = UserLanguagePreference.SQL_AND_PYTHON;
  } else if (rawLanguages.has("SQL")) {
    language = UserLanguagePreference.SQL;
  }
  return language;
};
